/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Colors, Typography, Img } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { SectionName, useAvroTracking } from '../../../observability';
import { COLOR_KEYS, MEDIA_MIN_WIDTH } from '../../../constants';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';
import { CustomButton } from '../../CustomButton';
import { RichTextHTMLString } from '../types';

export type TestimonialsSectionProps = {
  backgroundColor:
    | typeof COLOR_KEYS.moss
    | typeof COLOR_KEYS.neutral800
    | typeof COLOR_KEYS.night
    | typeof COLOR_KEYS.zapier;
  ctaText: string;
  ctaUrl: string;
  heading: string;
  description: RichTextHTMLString;
  testimonials: {
    author: string;
    logoAltText: string;
    logoUrl: string;
    quote: RichTextHTMLString;
    thumbnailAltText: string;
    thumbnailUrl: string;
  }[];
};

const Styles = labelStyles('TestimonialsSection', {
  root: (backgroundColor: TestimonialsSectionProps['backgroundColor']) =>
    css({
      padding: '64px 20px 40px',
      backgroundColor: Colors[backgroundColor],

      [MEDIA_MIN_WIDTH.small]: {
        padding: '64px 20px 64px',
      },

      [MEDIA_MIN_WIDTH.large]: {
        padding: '144px 20px 160px',
      },
    }),

  container: css({
    maxWidth: '1170px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '64px',

    [MEDIA_MIN_WIDTH.large]: {
      gridTemplateColumns: '280px 1fr',
      gap: '120px',
    },
  }),

  content: css({
    textAlign: 'center',

    [MEDIA_MIN_WIDTH.small]: {
      maxWidth: '320px',
      margin: '0 auto',
    },

    [MEDIA_MIN_WIDTH.large]: {
      height: 'fit-content',
      position: 'sticky',
      top: '144px',
      textAlign: 'left',
    },
  }),

  heading: css({
    ...Typography.pageHeader7,
    color: Colors.neutral100,
    marginBottom: '16px',

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.pageHeader5,
      marginBottom: '32px',
    },
  }),

  description: css({
    ...Typography.paragraph1Short,
    color: Colors.neutral100,
    marginBottom: '16px',

    [MEDIA_MIN_WIDTH.large]: {
      marginBottom: '40px',
    },
  }),

  testimonials: css({
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '30px',

    [MEDIA_MIN_WIDTH.small]: {
      gridTemplateColumns: '1fr 1fr',
    },
  }),

  testimonial: css({
    display: 'flex',
    flexDirection: 'column',
  }),

  testimonialThumbnail: css({
    height: '295px',
    width: '100%',
    display: 'block',
    objectFit: 'cover',
    borderRadius: '3px 3px 0 0',

    [MEDIA_MIN_WIDTH.small]: {
      height: '320px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      height: '345px',
    },

    [MEDIA_MIN_WIDTH.extraLarge]: {
      height: '370px',
    },
  }),

  testimonialBody: css({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '20px',
    backgroundColor: Colors.neutral100,
    borderRadius: '0 0 3px 3px',

    [MEDIA_MIN_WIDTH.large]: {
      padding: '30px',
    },
  }),

  testimonialLogo: css({
    margin: '-63px 0 20px',

    [MEDIA_MIN_WIDTH.large]: {
      margin: '-73px 0 20px',
    },
  }),

  testimonialContent: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flexGrow: 1,
  }),

  testimonialQuote: css({
    ...Typography.paragraph1Short,
    minHeight: '100px',
    marginBottom: '20px',
  }),

  testimonialAuthor: css({
    ...Typography.paragraph3Medium,
  }),
});

export function TestimonialsSection({
  backgroundColor,
  ctaText,
  ctaUrl,
  heading,
  description,
  testimonials,
}: TestimonialsSectionProps) {
  const { emitCTAClickEvent } = useAvroTracking();
  return (
    <div css={Styles.root(backgroundColor)}>
      <div css={Styles.container}>
        <div css={Styles.content}>
          <h2 css={Styles.heading}>{heading}</h2>
          <div css={Styles.description}>
            <RichTextHtml html={description} />
          </div>
          <CustomButton
            color="secondary"
            href={ctaUrl}
            isFullWidth={true}
            onClick={() =>
              emitCTAClickEvent({
                text: ctaText,
                href: ctaUrl,
                section: SectionName.TestimonialsSection,
              })
            }
          >
            {ctaText}
          </CustomButton>
        </div>
        <div css={Styles.testimonials}>
          {testimonials.map((testimonial, index) => (
            <div
              css={Styles.testimonial}
              key={`${testimonial.author}-${index}`}
            >
              <img
                alt={testimonial.thumbnailAltText}
                css={Styles.testimonialThumbnail}
                data-testid="testimonial-thumbnail"
                loading="lazy"
                src={testimonial.thumbnailUrl}
              />
              <div css={Styles.testimonialBody}>
                <div css={Styles.testimonialLogo}>
                  <Img
                    alt={testimonial.logoAltText}
                    borderRadius={3}
                    height={86}
                    isBlock={true}
                    objectFit="cover"
                    src={testimonial.logoUrl}
                    width={86}
                  />
                </div>
                <div css={Styles.testimonialContent}>
                  <div css={Styles.testimonialQuote}>
                    <RichTextHtml html={testimonial.quote} />
                  </div>
                  <div css={Styles.testimonialAuthor}>{testimonial.author}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
